import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { SplitterOnChangeEvent, SplitterPaneProps } from '@progress/kendo-react-layout';
import debounce from 'lodash/debounce';
import Card from '@components-clean/core/card';
import CoreSplitter from '@components-clean/core/core-splitter';
import { GridViewMenuItem } from '@components-clean/core/grid/grid-menu/grid-view-menu';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import DataViewDetails from '@pages-clean/admin/study/admin-data-views/data-views/details/data-view-details';
import DataViewList from '@pages-clean/admin/study/admin-data-views/data-views/list/data-view-list';
import {
    DataViewConfig,
    DataViewConfigForm,
    PanelData,
} from '@pages-clean/admin/study/admin-data-views/data-views/types';
import { DataFilterResultType } from '@pages-clean/admin/study/admin-data-views/filters/types';
import { PanelType } from 'types/graphql-global-types';
import { IUser } from '@contexts/user-context';
import { useTranslation } from '@hooks/index';
import { GridFullView, GridViewOptions, SplitterOrientation } from '@utils/constants';
import { GET_DATA_VIEW_CONFIGURATIONS } from '@queries/central-monitoring';
import {
    getPanels,
    dataReviewTables,
    dataViewConfigurations,
    dataViewConfigurationsVariables,
} from '@queries/generated/central-monitoring.generated';
import { StudyFields } from '@queries/generated/fragments.generated';

type DataViewsProps = {
    user: IUser;
    study: StudyFields;
    isLoading: boolean;
    panelsData?: getPanels;
    dataTablesData?: dataReviewTables;
    filtersData: DataFilterResultType[];
};

const DataViews = ({ user, study, isLoading, panelsData, dataTablesData, filtersData }: DataViewsProps) => {
    const [activeDataView, setActiveDataView] = useState<DataViewConfig | undefined>();
    const [dvConfigList, setDvConfigList] = useState<DataViewConfig[]>([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [gridCurrentView, setGridCurrentView] = useState<GridViewOptions>(SplitterOrientation.vertical);
    const [isReorderMode, setIsReorderMode] = useState(false);
    const [panels, setPanels] = useState<PanelData[]>([]);

    const { t }: { t: (key: string) => string } = useTranslation();

    const canEdit = user.canWriteSystemAdminLevel?.() ?? false;
    const studyRef = study?.id as string;

    const { data: dataViewConfigs, refetch } = useQuery<dataViewConfigurations, dataViewConfigurationsVariables>(
        GET_DATA_VIEW_CONFIGURATIONS,
        {
            variables: { studyId: studyRef },
        },
    );

    useEffect(() => {
        if (dataViewConfigs?.dataViewConfigurations) {
            setDvConfigList(dataViewConfigs?.dataViewConfigurations as DataViewConfig[]);

            // Clear out the active data view if it isn't found in the list.
            // This helps preserve row selection when coming back from row re-order
            if (activeDataView) {
                const dataViewConfig = dataViewConfigs.dataViewConfigurations.find(
                    (x) => x?.id === activeDataView.id,
                ) as DataViewConfig | undefined;
                setActiveDataView(dataViewConfig);
            }

            setIsReorderMode(false);
            setGridCurrentView(SplitterOrientation.vertical);
        }
    }, [dataViewConfigs, studyRef]);

    const reloadConfig = () => {
        refetch().catch((err) => window.console.error(err));
    };

    const onDelete = (id: string) => {
        setDvConfigList((old) => old.filter((x) => x?.id !== id));
        setActiveDataView(undefined);
    };

    const [filterState, setFilterState] = useState<CompositeFilterDescriptor>();

    const onUpdate = (dataView: DataViewConfig) => {
        setDvConfigList((state) => {
            return state.map((d) => {
                if (d.id === dataView.id) return dataView;
                return d;
            });
        });
    };

    const gridViewOptions: GridViewMenuItem[] = [
        {
            label: t('ra:fullView'),
            onClick: () => setGridCurrentView(GridFullView.full),
            icon: (
                <FontAwesomeIcon
                    icon={['fas', 'square']}
                    fontSize={18}
                    className="k-ml-1px k-mt--1px"
                    style={{ transform: 'scale(1.15,1)' }}
                />
            ),
            isActive: gridCurrentView === GridFullView.full,
        },
        {
            label: t('ra:topView'),
            onClick: () => setGridCurrentView(SplitterOrientation.vertical),
            icon: <FontAwesomeIcon icon={['far', 'window-maximize']} fontSize={18} className="k-mt--1px" />,
            isActive: gridCurrentView === SplitterOrientation.vertical,
        },
    ];
    const mapDataViewToForm = (dataView: DataViewConfig): DataViewConfigForm => {
        const mainPanel = panelsData?.panels?.find((item) => item?.id === dataView.mainPanelId);
        return {
            ...dataView,
            mainPanel: mainPanel
                ? {
                      id: mainPanel.id as string,
                      title: mainPanel.graph?.plotTitle,
                      active: mainPanel.active ?? false,
                      isModel: mainPanel.panelType === PanelType.MODEL,
                  }
                : undefined,
        };
    };

    const formMethods = useForm<DataViewConfigForm>({
        mode: 'all',
        defaultValues: activeDataView ? mapDataViewToForm(activeDataView) : undefined,
    });

    useEffect(() => {
        formMethods.reset(activeDataView ? mapDataViewToForm(activeDataView) : undefined);
    }, [activeDataView]);

    useEffect(() => {
        const validPanels = panelsData?.panels?.reduce<PanelData[]>((filtered, panel) => {
            // Data Views don't support Event Rate, Timeline and Proportion panels because they can only be used on the
            // Controls Hub so filter them out here and all other UI won't show them
            if (
                panel &&
                panel.panelType !== PanelType.EVENT_RATES_OVER_TIME &&
                panel.panelType !== PanelType.PROPORTIONS_OVER_TIME &&
                panel.panelType !== PanelType.TIMELINE
            ) {
                filtered.push({
                    active: panel.active ?? false,
                    id: panel.id as string,
                    title: panel.graph?.plotTitle ?? '',
                    isModel: panel.panelType === PanelType.MODEL,
                    panelType: panel.panelType,
                });
            }

            return filtered;
        }, []);

        setPanels(validPanels ?? []);
    }, [panelsData]);

    // Fixes an issue when adding a new record or editing an item causes the splitter to reset
    const [panes, setPanes] = useState<SplitterPaneProps[]>([{ size: '40%', min: '130px' }, { min: '120px' }]);
    const onPaneChange = (event: SplitterOnChangeEvent) => {
        debounce(() => setPanes(event.newState), 200);
    };

    return (
        <FormProvider {...formMethods}>
            <div>
                {(isLoading || !dataViewConfigs) && <Loading />}
                {!(isLoading || !dataViewConfigs) && (
                    <div style={{ height: 'calc(100vh - 152px)' }}>
                        <CoreSplitter
                            className="!k-h-full"
                            orientation={SplitterOrientation.vertical}
                            data-testid="data-view-splitter"
                            alignmentValues={
                                gridCurrentView === GridFullView.full || isReorderMode
                                    ? [
                                          {
                                              size: '100%',
                                              min: '130px',
                                          },
                                          { min: '300px', collapsed: true },
                                      ]
                                    : panes
                            }
                            onChange={onPaneChange}
                        >
                            <div data-testid="data-view-list-pane" className="k-h-full">
                                <DataViewList
                                    dataViewConfigData={dvConfigList}
                                    onDataViewChange={(dvDetails) => setActiveDataView(dvDetails)}
                                    study={study}
                                    onDataViewConfigAdd={reloadConfig}
                                    panelsData={panels}
                                    dataTablesData={dataTablesData}
                                    filtersData={filtersData}
                                    gridViewOptions={gridViewOptions}
                                    isEditMode={isEditMode}
                                    setIsEditMode={setIsEditMode}
                                    canEdit={canEdit}
                                    setGridCurrentView={(gridView) => setGridCurrentView(gridView)}
                                    isReorderMode={isReorderMode}
                                    setIsReorderMode={setIsReorderMode}
                                    gridCurrentView={gridCurrentView}
                                    gridFilterListener={(gridFilter) => {
                                        setFilterState(gridFilter);
                                    }}
                                    gridFilter={filterState}
                                    selectedItem={activeDataView}
                                />
                            </div>

                            {activeDataView?.recordId && (
                                <DataViewDetails
                                    dataViewConfig={activeDataView}
                                    existingViews={dvConfigList}
                                    panelsData={panels}
                                    dataTablesData={dataTablesData}
                                    filtersData={filtersData}
                                    isEditMode={isEditMode}
                                    setIsEditMode={setIsEditMode}
                                    onDelete={onDelete}
                                    onUpdate={onUpdate}
                                    user={user}
                                    studyId={study.id as string}
                                />
                            )}
                            {!activeDataView?.recordId && (
                                <Card
                                    data-testid="data-view-details-pane"
                                    title={t('admin:dataViewDetails')}
                                    className="k-h-full"
                                >
                                    <InfoField type="Info" message={t('admin:dataViewSelectInfo')} layout="center" />
                                </Card>
                            )}
                        </CoreSplitter>
                        )
                    </div>
                )}
            </div>
        </FormProvider>
    );
};
export default DataViews;
